import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Search, Loader2, Inbox, MessageCircle, ChevronRight } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { motion } from "framer-motion";
import LanguageToggle from "@/components/LanguageToggle";

const DoctorInbox = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const bn = language === "bn";
  const [conversations, setConversations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      setLoadError(null);
      const { data: convData, error } = await supabase
        .from("conversations")
        .select("*")
        .eq("practitioner_id", user.id)
        .eq("practitioner_type", "doctor")
        .order("last_message_at", { ascending: false });

      if (error || !convData) {
        setLoadError(error?.message ?? "Could not load conversations");
        setLoading(false);
        return;
      }

      const patientIds = [...new Set(convData.map((c) => c.patient_id))];
      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("user_id, full_name")
        .in("user_id", patientIds);
      if (profileError) {
        setLoadError(profileError.message);
        setLoading(false);
        return;
      }

      const profileMap: Record<string, string> = {};
      (profileData ?? []).forEach((p) => { profileMap[p.user_id] = p.full_name; });

      setConversations(convData.map((c) => ({ ...c, patient: { full_name: profileMap[c.patient_id] ?? "" } })));
      setLoading(false);
    };
    load();
  }, [user]);

  const filtered = conversations.filter(c => (c.patient?.full_name ?? "").toLowerCase().includes(search.toLowerCase()));

  return (
    <motion.div className="space-y-5 text-[#245853]" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <section className="relative min-h-[170px] overflow-hidden rounded-[28px] bg-[#edf7f3] px-5 pb-5 pt-5">
        <div className="relative z-10 flex items-start justify-between">
          <div>
            <h1 className="font-serif text-xl font-extrabold text-[#20534f]">{bn ? "ইনবক্স" : "Inbox"}</h1>
            <p className="mt-1 text-xs text-[#6d7978]">{bn ? "রোগীদের পরামর্শ কথোপকথন" : "Your patient conversations"}</p>
          </div>
          <LanguageToggle />
        </div>
        <img src="/inbox-messages.png" alt="" aria-hidden="true" className="pointer-events-none absolute bottom-0 right-0 h-[145px] w-[225px] object-contain object-right-bottom" />
      </section>

      <div className="relative">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#62736f]" />
          <Input
            placeholder={bn ? "রোগী খুঁজুন..." : "Search patients..."}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="h-11 rounded-full border border-[#e1e9e4] bg-white pl-10 text-sm shadow-none focus:border-[#2c8979] focus:ring-0"
          />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : loadError ? (
          <Card className="border border-[#e2ebe7] bg-white shadow-none">
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <p className="text-sm font-semibold text-destructive">{bn ? "ইনবক্স লোড করা যায়নি" : "Could not load inbox"}</p>
            <p className="mt-2 max-w-md text-xs text-muted-foreground">{loadError}</p>
            <button onClick={() => window.location.reload()} className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold text-primary">
              <Loader2 className="h-3 w-3" />{bn ? "আবার চেষ্টা করুন" : "Try again"}
            </button>
          </CardContent>
        </Card>
      ) : filtered.length === 0 ? (
        <Card className="border border-[#e2ebe7] bg-white shadow-none">
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#e1f2ed]">
              <Inbox className="h-7 w-7 text-[#398b82]" />
            </div>
            <p className="text-sm font-semibold text-foreground/70">{bn ? "কোন বার্তা নেই" : "No conversations yet"}</p>
            <p className="text-xs text-muted-foreground mt-1">{bn ? "অনুরোধ গ্রহণ করলে এখানে দেখা যাবে" : "Accepted consultations will appear here"}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(c => (
            <Card
              key={c.id}
              className="cursor-pointer border border-[#e2ebe7] bg-white shadow-none transition-colors hover:border-[#76b8ad]"
               onClick={() => navigate(`/doctor/chat/${c.id}`)}
            >
              <CardContent className="flex items-center gap-3 p-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#e1f2ed] text-sm font-bold text-[#398b82]">
                  {(c.patient?.full_name ?? "?").charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold truncate">{c.patient?.full_name || (bn ? "রোগী" : "Patient")}</p>
                  <p className="text-[11px] text-muted-foreground truncate flex items-center gap-1">
                    <MessageCircle className="h-3 w-3 shrink-0" />
                    {c.last_message || (bn ? "কথোপকথন শুরু করুন" : "Start the conversation")}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[10px] text-muted-foreground">
                    {c.last_message_at ? new Date(c.last_message_at).toLocaleDateString() : ""}
                  </span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default DoctorInbox;
